import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Validators } from '@angular/forms';
import { subformComponentProviders } from 'ngx-sub-form';
import { lastValueFrom } from 'rxjs';
import { unitsLib } from 'src/app/core/services/unit-library';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { LinearInterpSingle } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { InternalPressureProfileType } from '../../../models/internal-pressure-profile.model';

@Component({
  selector: 'app-influx-frac-depth-base',
  template: ``,
  providers: subformComponentProviders(InfluxFracDepthBaseComponent),
  standalone: false
})
export class InfluxFracDepthBaseComponent implements OnChanges {
  @Input() form;
  @Input() tubulars;
  @Input() userUnits;
  @Input() porePressures;
  @Input() fracPressures;
  @Input() influxDepthRange;
  @Input() fractureDepthDefault;
  @Input() currentString;
  @Input() smallestPipeId: number;
  
  private _defaultEmw: number;

  public validators: any;
  public liquidDensityValidation: { min: number, max: number };
  public fracDepthsRange: { min: number, max: number };
  public densityValidation: { min: number, max: number };
  public fractureGradientValidation: { min: number, max: number };
  public marginOfErrorValidation: { min: number, max: number };
  public gasGradientValidation: { min: number, max: number };
  public maxMudWeightValidation: { min: number, max: number };
  public kickVolumeValidation: { min: number, max: number };
  public kickIntensityValidation: { min: number, max: number };
  public mudGasInterfaceMin: number;
  public defaultEmw: number;
  public defaultFracGradient: number;
  public kickVolumeDefault: number;
  public defaultDrillPipeOd: number;
  public kickIntensityDefault: number;
  public liquidDensityDefault: number;
  public gasGradientDefault: number;
  public densityLabel: string;
  public gasGradientLabel: string;
  public volumeLabel: string;
  public lengthUnitLabel: string;

  constructor(private _trajectoryService: TrajectoryService, private _fluidsService: FluidsService) { }

  ngOnChanges(changes: SimpleChanges) {
    this.fracDepthsRange = { ...this.influxDepthRange };
    this.densityLabel = unitsLib[this.userUnits.density].symbol;
    this.gasGradientLabel = this.userUnits.gasGradient;
    this.volumeLabel = this.userUnits.volume;
    this.lengthUnitLabel = this.userUnits.longLengths;

    this.mudGasInterfaceMin = this.userUnits.longLengths === 'ft' ? 0.1 : 0.03;

    switch (this.volumeLabel) {
      case 'bbl':
        this.kickVolumeValidation = { min: 0, max: 500 };
        this.kickVolumeDefault = 50;
        break;
      case 'm³':
        this.kickVolumeValidation = { min: 0, max: 79.4 };
        this.kickVolumeDefault = 8;
        break;
      case 'l':
        this.kickVolumeValidation = { min: 0, max: 79494 };
        this.kickVolumeDefault = 8000;
        break;
    }

    switch (this.userUnits.shortLengths) {
      case 'in':
        this.defaultDrillPipeOd = 3.5;
        break;
      case 'cm':
        this.defaultDrillPipeOd = 8.89;
        break;
      case 'mm':
        this.defaultDrillPipeOd = 88.9;
        break;
    }

    switch (this.densityLabel) {
      case 'ppg':
        this.liquidDensityValidation = { min: 0.01, max: 25 };
        this.maxMudWeightValidation = { min: 0.1, max: 25 };
        this.kickIntensityDefault = 0.5;
        this.kickIntensityValidation = { min: 0, max: 5 };
        this.densityValidation = { min: 1.04, max: 25 };
        this.fractureGradientValidation = { min: 1, max: 25 };
        this.marginOfErrorValidation = { min: 0, max: 10 };
        this._defaultEmw = 8.33;
        this.liquidDensityDefault = 8.33;
        break;
      case 'kg/m³':
      case 'g/L':
        this.liquidDensityValidation = { min: 1.19, max: 2995 };
        this.maxMudWeightValidation = { min: 0.1, max: 2995 };
        this.kickIntensityDefault = 60;
        this.kickIntensityValidation = { min: 0, max: 599 };
        this.densityValidation = { min: 124, max: 2995 };
        this.fractureGradientValidation = { min: 119.8, max: 2995 };
        this.marginOfErrorValidation = { min: 0, max: 1198 };
        this._defaultEmw = 1000;
        this.liquidDensityDefault = 1000;
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.liquidDensityValidation = { min: 0.00119, max: 3 };
        this.maxMudWeightValidation = { min: 0.012, max: 3 };
        this.kickIntensityDefault = 0.06;
        this.kickIntensityValidation = { min: 0, max: 0.6 };
        this.densityValidation = { min: 0.12, max: 3 };
        this.fractureGradientValidation = { min: 0.119, max: 3 };
        this.marginOfErrorValidation = { min: 0, max: 1.2 }
        this._defaultEmw = 1;
        this.liquidDensityDefault = 1;
        break;
      case 'g/m³':
        this.liquidDensityValidation = { min: 1198, max: 2995660 };
        this.maxMudWeightValidation = { min: 100, max: 2995660 };
        this.kickIntensityDefault = 60000;
        this.kickIntensityValidation = { min: 0, max: 599000 };
        this.densityValidation = { min: 124000, max: 2995660 };
        this.fractureGradientValidation = { min: 119826, max: 2995660 };
        this.marginOfErrorValidation = { min: 0, max: 1198264 };
        this._defaultEmw = 1000000;
        this.liquidDensityDefault = 1000000;
        break;
    }

    switch (this.gasGradientLabel) {
      case 'psi/ft':
        this.gasGradientValidation = { min: 0, max: 5.1948 };
        this.gasGradientDefault = 0.1;
        break;
      case 'kPa/m':
        this.gasGradientValidation = { min: 0, max: 117.5 };
        this.gasGradientDefault = 2.26;
        break;
      case 'sg':
        this.gasGradientValidation = { min: 0, max: 11.97 };
        this.gasGradientDefault = 0.23;
        break;
      case 'bar/m':
        this.gasGradientValidation = { min: 0, max: 1.17 };
        this.gasGradientDefault = 0.022;
        break;
    }

    this.validators = {
      porePressure: [Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max), Validators.required],
      marginOfError: [Validators.min(this.marginOfErrorValidation.min), Validators.max(this.marginOfErrorValidation.max), Validators.required],
      influxDepth: [Validators.min(this.influxDepthRange.min), Validators.max(this.influxDepthRange.max), Validators.required],
      fractureDepth: [Validators.min(this.fracDepthsRange.min), Validators.max(this.fracDepthsRange.max), Validators.required],
      fractureGradient: [Validators.min(this.fractureGradientValidation.min), Validators.max(this.fractureGradientValidation.max), Validators.required],
      fractureMarginOfError: [Validators.min(this.marginOfErrorValidation.min), Validators.max(this.marginOfErrorValidation.max), Validators.required],
      gasGravity: [Validators.min(0), Validators.max(2), Validators.required],

      kickVolume: [Validators.min(this.kickVolumeValidation.min), Validators.max(this.kickVolumeValidation.max), Validators.required],
      kickIntensity: [Validators.min(this.kickIntensityValidation.min), Validators.max(this.kickIntensityValidation.max), Validators.required],
      maxMudWeight: [Validators.min(this.maxMudWeightValidation.min), Validators.max(this.maxMudWeightValidation.max), Validators.required],
      drillPipeOd: [Validators.min(0), Validators.max(this.smallestPipeId), Validators.required],
      liquidDensity: [Validators.min(this.liquidDensityValidation.min), Validators.max(this.liquidDensityValidation.max), Validators.required],

      mudGasInterface: [Validators.min(this.mudGasInterfaceMin), Validators.max(this.influxDepthRange.max), Validators.required],
    };

    if (changes.currentString || changes.fracPressures) {
      this.getEmwDefaultFrac(this.influxDepthRange.max, this.fractureDepthDefault, false, false);
    }
  }

  async getEmwDefaultFrac(influxDepth, fracDepth, updatePorePressure: boolean, updateFracGradient: boolean): Promise<void> {
    const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([influxDepth, fracDepth]));

    const hasFrac = this.fracPressures?.length > 1;
    const hasPore = this.porePressures?.length > 1;

    if (hasFrac) {
      this.defaultFracGradient = hasFrac ? +LinearInterpSingle(tvds[1], this.fracPressures, this.userUnits).toFixed(2) : 0;
    } else {
      this.defaultFracGradient = this.currentString.annularFluid.state.nominalDensity + GetValueFromPpg(2, this.densityLabel);
    }

    if (hasPore) {      
      this.defaultEmw = hasPore ? +LinearInterpSingle(tvds[0], this.porePressures, this.userUnits).toFixed(2) : 0;
    } else {      
      this.defaultEmw = this._defaultEmw;
    }

    if (!this.form.formGroup.controls.fractureGradient.value?.control || updateFracGradient) {
      this.form.formGroup.controls.fractureGradient.setValue({ control: this.defaultFracGradient });
    }

    if (this.form.formGroup.controls.porePressure && !this.form.formGroup.controls.porePressure.value?.control || updatePorePressure) {
      this.form.formGroup.controls.porePressure.setValue({ control: this.defaultEmw });
    }
  }

  public gasGravityChange() {
    if (this.form.formGroup.controls.useGasGravity.value) {
      this.form.formGroup.controls.useGasGravity.setValue(true);
    } else {
      this.form.formGroup.controls.useGasGravity.setValue(false);
    }
  }

  public influxDepthChange() {
    if (!this.form.formGroup.controls.influxDepth.invalid) {
      this.getEmwDefaultFrac(this.form.formGroup.controls.influxDepth.value.control, 0, true, false);
    }

    this.updateRangeAndFluidDensity();
  }

  public fractureDepthChange() {
    if (!this.form.formGroup.controls.fractureDepth.invalid) {
      this.getEmwDefaultFrac(0, this.form.formGroup.controls.fractureDepth.value.control, false, true);
    }
  }

  public fractureGradientChange() {
    if (!this.form.formGroup.controls.fractureGradient.value?.control) {
      this.getEmwDefaultFrac(0, this.form.formGroup.controls.fractureDepth.value.control, false, true);
    }
  }

  public porePressureChange() {
    if (!this.form.formGroup.controls.porePressure.value?.control) {
      this.getEmwDefaultFrac(this.form.formGroup.controls.influxDepth.value.control, 0, true, false);
    }
  }

  public updateRangeAndFluidDensity(): void {
    let annularFluidId = null;
    const currentStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);

    for (let i = currentStringIndex; i < this.tubulars.length; i++) {
      const tubular = this.tubulars[i];
      if (tubular.shoeMd >= this.form.formGroup.controls.influxDepth.value.control) {
        annularFluidId = tubular.annularFluidId;

        if (tubular.type == 'Tieback') {
          const linerItsTiedBackTo = this.tubulars.find(t =>
            t.type === 'Liner' &&
            t.hangerMd === tubular.shoeMd
          ).shoeMd;
          this.fracDepthsRange.max = linerItsTiedBackTo;
          continue;
        } else if (tubular.type == 'Liner' || tubular.type == 'Casing') {
          this.fracDepthsRange.max = tubular.shoeMd;

          for (let j = i - 1; j >= 0; j--) {
            if (this.tubulars[j].type == 'Tieback') {
              continue;
            } else {
              this.fractureDepthDefault = this.tubulars[j].shoeMd;
              break;
            }
          }
          break;
        } else {
          this.fracDepthsRange.max = tubular.shoeMd;
        }
      }
    }

    if (this.fracDepthsRange.max >= this.fracDepthsRange.min) {
      this.fracDepthsRange.max = this.form.formGroup.controls.influxDepth.value?.control ?? this.fracDepthsRange.max;
    }

    if (Math.abs(this.fracDepthsRange.min - this.fracDepthsRange.max) < 0.0001) {
      this.form.formGroup.controls.fractureDepth.setValue({ control: this.fracDepthsRange.min });
      this.fractureDepthDefault = this.fracDepthsRange.min;
    }

    // if (this.form.formGroup.controls.influxDepth.value?.control < this.form.formGroup.controls.fractureDepth.value?.control) {
      this.form.formGroup.controls.fractureDepth.setValue({ control: this.fractureDepthDefault });
      this.getEmwDefaultFrac(0, this.fractureDepthDefault, false, true);
    // }
    const fractureDepthValid = !this.form.formGroup.controls.fractureDepth.invalid;

    this.validators.fractureDepth = [Validators.min(this.fracDepthsRange.min), Validators.max(this.form.formGroup.controls.influxDepth.value?.control), Validators.required];
    this.form.formGroup.controls.fractureDepth.updateValueAndValidity();

    this.validators.mudGasInterface = [Validators.min(this.mudGasInterfaceMin), Validators.max(this.form.formGroup.controls.influxDepth.value?.control), Validators.required];
    this.form.formGroup.controls.mudGasInterface.updateValueAndValidity();

    if (!fractureDepthValid) {
      setTimeout(() => {
        this.fractureDepthChange();
      }, 100);
    }

    if (this.form.formGroup.controls.type && (this.form.formGroup.controls.type.value == InternalPressureProfileType.GASKICK ||
      this.form.formGroup.controls.type.value == InternalPressureProfileType.LIQUIDKICK)
    ) {
      if (annularFluidId && annularFluidId !== this.currentString.annularFluidId) {
        this._fluidsService.getFluidById(annularFluidId).subscribe(fluid => {
          this.form.formGroup.controls.maxMudWeight.setValue({ control: fluid.state['nominalDensity'] });
        });
      } else {
        this.form.formGroup.controls.maxMudWeight.setValue({ control: this.currentString.annularFluid?.state['nominalDensity'] ?? 0 });
      }
    }
  }
}
